import { useEffect, useId, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { cn } from '../../lib/utils'
import { useTheme } from '../../contexts/ThemeContext'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  className?: string
  /** When set, renders a header row with this title and a close control. */
  title?: string
}

export function Modal({ isOpen, onClose, children, className, title }: ModalProps) {
  const { colors } = useTheme()
  const reduceMotion = useReducedMotion()
  const titleId = useId()

  useEffect(() => {
    const previousOverflow = document.body.style.overflow

    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = previousOverflow
    }

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  const overlayMotion = reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }

  const panelMotion = reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, y: 24, scale: 0.98 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: 16, scale: 0.98 },
      }

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            {...overlayMotion}
            onClick={onClose}
            transition={{ duration: reduceMotion ? 0.01 : 0.18 }}
            className="fixed inset-0 z-[100] bg-black/45 backdrop-blur-[6px]"
          />
          <div className="fixed inset-0 z-[100] overflow-y-auto pt-[max(0.75rem,env(safe-area-inset-top,0px))] pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] pl-[max(0.75rem,env(safe-area-inset-left,0px))] pr-[max(0.75rem,env(safe-area-inset-right,0px))] sm:pt-[max(1rem,env(safe-area-inset-top,0px))] sm:pb-[max(1rem,env(safe-area-inset-bottom,0px))] sm:pl-[max(1rem,env(safe-area-inset-left,0px))] sm:pr-[max(1rem,env(safe-area-inset-right,0px))]">
            <div className="flex min-h-full items-start justify-center py-3 sm:items-center sm:py-6">
              <motion.div
                {...panelMotion}
                transition={{
                  duration: reduceMotion ? 0.01 : 0.24,
                  ease: [0.2, 0.8, 0.2, 1],
                }}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
                className={cn(
                  'flex max-h-[min(88vh,48rem)] w-full flex-col overflow-hidden rounded-[28px] sm:max-w-md sm:rounded-[32px]',
                  title && 'sm:max-w-2xl lg:max-w-3xl',
                  className,
                )}
                style={{
                  backgroundColor: colors.surface,
                  border: `1px solid ${colors.border}CC`,
                  boxShadow: `0 24px 80px ${colors.shadow}32`,
                  color: colors.text,
                  paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))',
                }}
              >
                {title ? (
                  <>
                    <div
                      className="flex flex-shrink-0 items-center justify-between rounded-t-[28px] border-b px-4 py-4 sm:rounded-t-[32px] sm:px-6"
                      style={{
                        backgroundColor: colors.background,
                        borderColor: colors.border,
                      }}
                    >
                      <h2
                        id={titleId}
                        className="text-xl font-bold pr-2"
                        style={{ color: colors.text }}
                      >
                        {title}
                      </h2>
                      <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex min-h-11 min-w-11 flex-shrink-0 items-center justify-center rounded-xl transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 motion-reduce:transition-none"
                        style={{ color: colors.textSecondary }}
                        aria-label="Close dialog"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = `${colors.primary}14`
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent'
                        }}
                      >
                        <XMarkIcon className="h-5 w-5" aria-hidden />
                      </button>
                    </div>
                    <div
                      className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-6"
                    >
                      {children}
                    </div>
                  </>
                ) : (
                  <div className="min-h-0 overflow-y-auto overscroll-contain">{children}</div>
                )}
              </motion.div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  )
}

